<script lang="ts">
  import { onMount } from 'svelte';
  import { getServiceWorkerManager } from '../lib/service-worker-manager';

  let updateAvailable = $state(false);
  let offlineReady = $state(false);
  let isUpdating = $state(false);
  let dismissedOffline = $state(false);
  let wb: any = null;

  const swManager = getServiceWorkerManager();

  onMount(async () => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    if (import.meta.env.PROD) {
      try {
        const { Workbox } = await import('workbox-window');
        wb = new Workbox('/sw.js');

        wb.addEventListener('waiting', () => {
          updateAvailable = true;
        });

        wb.addEventListener('controlling', () => {
          window.location.reload();
        });

        wb.addEventListener('activated', (event: any) => {
          // First activation - app is ready for offline use
          if (!event.isUpdate) {
            offlineReady = true;
            // Auto-dismiss after 5 seconds
            setTimeout(() => {
              dismissedOffline = true;
            }, 5000);
          }
        });

        await wb.register();
        console.log('Service Worker registered successfully');
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }

      // Also use the service worker manager for additional functionality
      swManager.register('/sw.js').catch((error) => {
        console.error('Service Worker Manager registration failed:', error);
      });
    }
  });

  function handleUpdate() {
    if (!wb) return;

    isUpdating = true;

    wb.addEventListener('controlling', () => {
      window.location.reload();
    });

    wb.messageSkipWaiting();
  }

  function dismissUpdate() {
    updateAvailable = false;
  }

  function dismissOffline() {
    dismissedOffline = true;
  }
</script>

<!-- Update Available Notification -->
{#if updateAvailable}
  <div class="fixed bottom-4 right-4 p-4 bg-blue-600 text-white rounded-lg shadow-lg 
              z-50 max-w-sm animate-slide-up">
    <div class="flex items-start justify-between mb-2">
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        <p class="font-semibold">Update Available</p>
      </div>
      <button
        onclick={dismissUpdate}
        class="text-white hover:text-gray-200 transition-colors"
        aria-label="Dismiss"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
    <p class="text-sm mb-3 text-blue-100">
      A new version of Legere is ready to install.
    </p>
    <div class="flex gap-2">
      <button
        onclick={handleUpdate}
        disabled={isUpdating}
        class="flex-1 px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-gray-100 
               disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
      >
        {isUpdating ? 'Updating...' : 'Update Now'}
      </button>
      <button
        onclick={dismissUpdate}
        class="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 
               transition-colors"
      >
        Later
      </button>
    </div>
  </div>
{/if}

<!-- Offline Ready Notification -->
{#if offlineReady && !dismissedOffline}
  <div class="fixed bottom-4 right-4 p-4 bg-green-600 text-white rounded-lg shadow-lg 
              z-50 max-w-sm animate-slide-up">
    <div class="flex items-start justify-between mb-2">
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="font-semibold">Ready for Offline Use</p>
      </div>
      <button
        onclick={dismissOffline}
        class="text-white hover:text-gray-200 transition-colors"
        aria-label="Dismiss"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
    <p class="text-sm text-green-100">
      Legere can now work offline. Your books will be available even without an internet connection.
    </p>
  </div>
{/if}

<style>
  @keyframes slide-up {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .animate-slide-up {
    animation: slide-up 0.3s ease-out;
  }
</style>
